/**
 * The streak ladder: how loud each rung is, and what colour it burns.
 *
 * Shared by the aura (`./streakAura`) and the badge (`./streakBadge`) so the
 * rim around the table and the callout in front of it can never disagree about
 * what a run is worth. Anything else that wants to react to a streak — the
 * letterbox spill, a future hit-stop — should read from here too.
 *
 * ## Why the intensity is a table and not a formula
 *
 * The obvious curve is linear in the level, and it is wrong for this game.
 *
 * The streak levels are 1..5, shown as the 2x..6x callouts. But **5x and 6x are
 * very nearly unreachable**: getting there needs two wilds on top of an already
 * running 4x, which almost never happens. A linear ramp spends its top half on
 * rungs a player will hardly ever see and leaves the rungs they actually reach —
 * 3x, 4x, 5x — looking like warm-ups for a payoff that never arrives.
 *
 * So the ramp is front-loaded. The biggest single jump in the whole ladder is
 * **2x to 3x**, and by 4x the effect is already at four fifths of everything it
 * has. The top two rungs still escalate, but they are a flourish on a payoff
 * that has already landed rather than the payoff itself.
 *
 * A table also makes the intent inspectable: the numbers below *are* the design,
 * and tuning the feel means editing them rather than reverse-engineering an
 * exponent.
 *
 * ## The colour ramp
 *
 * Gold is held deliberately far up the ladder, and violet appears only at the
 * very top. Two reasons:
 *
 * 1. **Contrast.** The felt is deep plum (`palette.felt`, 0x1b1030). Violet on
 *    plum is violet on violet — it fills the frame without ever separating from
 *    it. Gold and rose are the two hues on this table that read hardest against
 *    that background, so the reachable rungs get them.
 * 2. **Meaning.** Violet already means "wild" everywhere else in this game.
 *    Spending it at 6x keeps it rare, which is the point of 6x.
 *
 * Luminance climbs alongside hue across the reachable rungs, so the ladder
 * escalates on brightness as well as colour — that is what keeps 5x
 * distinguishable from 4x once both are warm. See `TARGET_LUMA` for why the top
 * rung is deliberately exempt.
 */

import { palette } from '@/core/tokens';

/** Highest rung, i.e. the 6x callout. */
export const LADDER_MAX = 5;

/**
 * Effect intensity per rung, 0..1, indexed by `level - 1`.
 *
 * Read this as the design brief: 2x announces, 3x is the moment the run starts
 * to feel real, 4x is nearly everything, and the last two are flourish.
 */
const INTENSITY = [0.3, 0.58, 0.8, 0.93, 1] as const;

/**
 * Base hue per rung. Gold through the reachable middle, heating as it climbs,
 * violet reserved for the top. See the note above on contrast against plum.
 */
const HUES = [
  palette.gold, //        2x — money
  0xffb347, //            3x — gold pushed hot
  0xff9d5c, //            4x — amber, the run is burning
  0xff7a8a, //            5x — hot
  palette.violetLight, // 6x — rare
] as const;

/**
 * Target perceptual luminance per rung.
 *
 * The second half of the ramp, and the reason it is a *target* rather than a
 * fixed amount of added white: **saturated hot hues are intrinsically darker
 * than gold.** Gold sits at 0.78 luminance, a saturated rose at 0.41. Heating
 * the hue while adding a constant white lift therefore produces a ladder that
 * climbs in colour and *falls* in brightness — the opposite of building
 * momentum, and not something you would notice by looking at swatches.
 *
 * Naming the luminance instead and solving for the white mix makes the climb
 * true by construction, and stays true if the hues above are ever retuned.
 *
 * **The top rung is deliberately the exception.** Gold is the brightest
 * saturated colour on this palette, so driving violet past it needs about 70%
 * white — at which point it is lavender, and the rarity that made violet worth
 * reserving for 6x is gone. So the brightness climb runs across the *reachable*
 * rungs, which is where it does the work, and 6x trades brightness for hue.
 */
const TARGET_LUMA = [0.78, 0.8, 0.82, 0.85, 0.79] as const;

/** Rec. 709 relative luminance of a 0xRRGGBB colour, 0..1. */
function luminance(hex: number): number {
  const r = ((hex >> 16) & 0xff) / 255;
  const g = ((hex >> 8) & 0xff) / 255;
  const b = (hex & 0xff) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Mix a colour toward white until it reaches `target` luminance.
 *
 * Mixing toward white moves luminance linearly from `luminance(c)` to 1, so the
 * required amount is a closed form rather than a search.
 */
function liftToLuma(hex: number, target: number): number {
  const base = luminance(hex);
  if (base >= target) return hex;
  const k = Math.min(Math.max((target - base) / (1 - base), 0), 1);
  return mixRgb(hex, palette.white, k);
}

/** The rung colours, luminance-corrected once at module load. */
const RUNG_COLOURS = HUES.map((hue, i) => liftToLuma(hue, TARGET_LUMA[i]));

/** Linear blend between two 0xRRGGBB colours. */
export function mixRgb(a: number, b: number, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  return (
    (Math.round(ar + (br - ar) * k) << 16) |
    (Math.round(ag + (bg - ag) * k) << 8) |
    Math.round(ab + (bb - ab) * k)
  );
}

/**
 * Interpolate a table at a fractional level.
 *
 * The aura eases between rungs rather than stepping, so it asks for values at
 * e.g. level 2.4 and must get something continuous back.
 */
function sampleAt(table: readonly number[], level: number): { a: number; b: number; t: number } {
  const clamped = Number.isFinite(level) ? Math.min(Math.max(level, 0), LADDER_MAX) : 0;
  if (clamped <= 1) {
    // Below the first rung the ladder fades toward nothing rather than
    // snapping: level 0 is "no run", and the aura passes through it.
    return { a: table[0], b: table[0], t: 0 };
  }
  const lower = Math.min(Math.floor(clamped), LADDER_MAX) - 1;
  const upper = Math.min(lower + 1, LADDER_MAX - 1);
  return { a: table[lower], b: table[upper], t: clamped - Math.floor(clamped) };
}

/**
 * Effect intensity at a level, 0..1.
 *
 * Below level 1 it scales down to 0 so an aura fading out loses its intensity
 * as well as its opacity.
 */
export function momentumAt(level: number): number {
  const clamped = Number.isFinite(level) ? Math.min(Math.max(level, 0), LADDER_MAX) : 0;
  if (clamped <= 0) return 0;
  if (clamped < 1) return INTENSITY[0] * clamped;
  const { a, b, t } = sampleAt(INTENSITY, clamped);
  return a + (b - a) * t;
}

/** Effect intensity at an integer rung. Convenience for one-shot callers. */
export function momentum(level: number): number {
  return momentumAt(Math.round(level));
}

/** The colour a level burns, luminance lift included. */
export function ladderColourAt(level: number): number {
  const clamped = Number.isFinite(level) ? Math.min(Math.max(level, 0), LADDER_MAX) : 0;
  const { a, b, t } = sampleAt(RUNG_COLOURS, clamped);
  return mixRgb(a, b, t);
}

/** The colour an integer rung burns. */
export function ladderColour(level: number): number {
  return ladderColourAt(Math.round(level));
}

/** 0..1 red/green/blue, for shader uniforms. */
export function ladderColourRgb(level: number): [number, number, number] {
  const hex = ladderColourAt(level);
  return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
}

/* ------------------------------------------------------------- hit-stop -- */

/**
 * How long, and how hard, a rung freezes the table: `[seconds, timeScale]`.
 *
 * The header above promised this a home here — "anything else that wants to
 * react to a streak … should read from here too" — and a hit-stop is the
 * clearest case of all, because it is *nothing but* a statement about how loud
 * a rung is.
 *
 * It is also the channel that costs no screen area at all, which is why it is
 * worth having. Fighting games settled "make this hit read as bigger" decades
 * ago and the answer was never to scale anything up: both parties freeze for a
 * few frames at contact, which gives the eye time to register that a collision
 * happened. Measured frame counts run about 9 frames for a light hit and 13 for
 * a heavy one; in Smash the freeze scales with damage, so the size of the pause
 * *is* the size of the hit.
 *
 * Driven by {@link momentum} rather than by the level, like every other channel
 * here, so the front-loading in `INTENSITY` reaches it too:
 *
 * | rung | seconds | frames at 60Hz | time scale |
 * |---|---|---|---|
 * | 2x | 0.078 | 4.7 | 0.25 |
 * | 3x | 0.095 | 5.7 | 0.21 |
 * | 4x | 0.108 | 6.5 | 0.17 |
 * | 5x | 0.116 | 7.0 | 0.15 |
 * | 6x | 0.120 | 7.2 | 0.14 |
 *
 * **A bust still freezes harder than any of these** (`HIT_STOP.bust` in
 * `@/main` is 0.09 s at 0.08). That is deliberate: a streak holds *longer* and
 * *shallower*, so a reward and a mistake read as two different kinds of weight
 * rather than as the same effect at two settings.
 */
export function hitStopAt(level: number): readonly [number, number] {
  const k = momentum(level);
  return [0.06 + 0.06 * k, 0.3 - 0.16 * k];
}

/**
 * The ramp back to normal speed after a rung's freeze: `[seconds, timeScale]`,
 * or `null` for a rung that simply snaps back.
 *
 * A hit-stop says *a thing happened*. Coming out of it slowly says *that thing
 * mattered*, and it is the cheapest drama in the box because it borrows time
 * the freeze has already taken rather than asking for more screen or more
 * seconds of hold.
 *
 * **Only the top two rungs get one, and that is the whole design.** If every
 * rung ramped, a fast player would spend a three-minute round wading, and the
 * ladder would have bought its drama by making the game worse to play. Two
 * firings a round at most, on the two events that are actually rare, is the
 * budget this can afford.
 *
 * Kept clear of {@link hitStopAt}'s territory in both directions. A bust
 * freezes hardest and snaps — a mistake is abrupt and over. A streak freezes
 * longer and shallower and then *eases out*, so the two remain different kinds
 * of weight rather than the same effect at two settings, which is the property
 * the hit-stop ladder was built to protect.
 *
 * | rung | freeze | then | total dilation |
 * |---|---|---|---|
 * | 2x-4x | 0.078-0.108 s | snap | — |
 * | 5x | 0.116 s @ 0.15 | 0.13 s @ 0.62 | 0.246 s |
 * | 6x | 0.120 s @ 0.14 | 0.16 s @ 0.55 | 0.280 s |
 */
export function settleAt(level: number): readonly [number, number] | null {
  const rung = Math.round(Number.isFinite(level) ? level : 0);
  if (rung < 4) return null;
  const k = momentum(rung);
  return [0.10 + 0.06 * k, 0.68 - 0.13 * k];
}
